// Tenant screen customization — shared types, the host transport contract and
// the pure helpers General Processes use (criteria / grid settings). The
// transaction diff builders live in ./txnOps, the editing session in
// ./session. What may be changed is decided by the server's overlay_policy.

export type CustomizeObjType = 'T' | 'G' | 'R'

export interface ModelOverlayResult {
  obj_type: string
  obj_name: string
  core: any
  overlay: any
  effective: any
  version: number
}

export interface OverlaySetLeafLike {
  path: string
  value: unknown
  key?: string
}

/** One array change of a Make-changes step (the build transport only —
 *  an overlay cannot add, remove or move items). `array` is a Customize-style
 *  path to the list (`transaction.sql_models.@items.<form>.columns`); `key`
 *  / `before` name items by their key field (col_id, name, …). */
export interface BuildOp {
  op: 'insert' | 'remove' | 'move'
  array: string
  item?: Record<string, unknown>
  key?: string
  /** Insert / move before this item; absent = at the end. */
  before?: string | null
  /** remove only: a field deleted WITH its stored values (confirmed). */
  drop_data?: boolean
  /** remove only (server inverses): take away the list if it is now empty. */
  prune?: boolean
}

/** What a step carries beyond set / reset (the build transport reads it;
 *  an overlay transport ignores it). */
export interface PatchExtras {
  ops?: BuildOp[]
  /** Apply set / reset before ops (an inverse's order). Default: ops first. */
  leaves_first?: boolean
  /** Inverse only: objects to remove again once a reset leaves them empty. */
  prune?: Record<string, number>
}

/** What a transport's patch may answer. The build transport returns the
 *  server-computed `inverse` (a core model cannot be inverted from an overlay
 *  document) and the new `effective` model. */
export interface PatchResult {
  inverse?: { set: OverlaySetLeafLike[]; reset: string[] } & PatchExtras
  effective?: unknown
  core?: unknown
  overlay?: unknown
  warnings?: string[]
}

/** Host transport: the platform's /livepreview/model-overlay/* or the
 *  downloaded runtime's /app/<project>/api/model-overlay/* (Customize), or
 *  the platform's /designers/transaction/<project>/<obj>/build-* (Make
 *  changes, the core model). */
export interface CustomizeTransport {
  get(objType: CustomizeObjType, objName: string): Promise<ModelOverlayResult>
  patch(objType: CustomizeObjType, objName: string,
        set: OverlaySetLeafLike[], reset: string[], extras?: PatchExtras): Promise<unknown>
  reset(objType: CustomizeObjType, objName: string): Promise<unknown>
}

export function truthy(v: unknown): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') return ['1', 'true', 'yes', 'y'].includes(v.trim().toLowerCase())
  return false
}

export function settingsFor(map: any, name: string): any {
  if (!map || typeof map !== 'object') return {}
  const k = Object.keys(map).find((x) => x.toLowerCase() === name.toLowerCase())
  return k ? map[k] || {} : {}
}

export function humanise(name: string): string {
  return name.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

// ── general processes ────────────────────────────────────────────────────

const G_PLACEHOLDER = /\?\.([A-Za-z_][A-Za-z0-9_]*)/g

export function processCriterionNames(process: any): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  const sql = String(process?.source_sql ?? '')
  for (const m of sql.matchAll(G_PLACEHOLDER)) {
    const lc = m[1].toLowerCase()
    if (!seen.has(lc)) { seen.add(lc); out.push(m[1]) }
  }
  return out
}

/** A General Process criteria prompt as the form renders it. */
export interface ProcessCriterionLike {
  name: string
  label?: string
  value?: unknown
  default?: unknown
  hidden?: boolean
  protect?: boolean
  order?: number
  [k: string]: unknown
}

/** Apply a process's `criteria_settings` to its derived prompts — the TS twin
 *  of `screen_settings.process_criteria_items` (label, default, hidden,
 *  read-only, order; unordered prompts keep their SQL position). */
export function applyCriteriaSettings<T extends ProcessCriterionLike>(
  items: T[], settings: Record<string, any> | null | undefined,
): T[] {
  if (!settings || typeof settings !== 'object' || !Object.keys(settings).length) return items
  const out = items.map((item, idx) => {
    const s = settingsFor(settings, item.name)
    const d: T = { ...item }
    if (String(s.label ?? '').trim()) d.label = String(s.label).trim()
    const dv = s.default
    if (dv !== undefined && dv !== null && String(dv).trim() !== '') { d.value = dv; d.default = dv }
    d.hidden = truthy(s.hidden)
    d.protect = truthy(s.protect)
    const n = Number(s.order)
    d.order = s.order !== undefined && s.order !== '' && Number.isFinite(n) ? n : (idx + 1) * 10
    return { d, idx }
  })
  out.sort((a, b) => (a.d.order! - b.d.order!) || (a.idx - b.idx))
  return out.map((x) => x.d)
}

/** A process's result-grid settings, keyed by lower-cased column. */
export function processColumnSettings(process: any): Record<string, { order?: number; hidden: boolean; protect: boolean }> {
  const raw = process?.column_settings
  const out: Record<string, { order?: number; hidden: boolean; protect: boolean }> = {}
  if (!raw || typeof raw !== 'object') return out
  for (const [col, s] of Object.entries<any>(raw)) {
    if (!s || typeof s !== 'object') continue
    const n = Number(s.order)
    out[col.trim().toLowerCase()] = {
      hidden: truthy(s.hidden), protect: truthy(s.protect),
      ...(s.order !== undefined && s.order !== '' && Number.isFinite(n) ? { order: n } : {}),
    }
  }
  return out
}

/** Errors the server listed for a refused customization (422). */
export function policyErrors(e: any): string[] {
  const d = e?.response?.data?.detail ?? e?.detail ?? e?.body?.detail
  const errs = d?.error?.details?.errors ?? d?.details?.errors
  if (Array.isArray(errs) && errs.length) return errs.map(String)
  const msg = d?.error?.message ?? (typeof d === 'string' ? d : null) ?? e?.message
  return [String(msg || 'Could not save the customization')]
}
